import { createActionGroup, createFeature, createReducer, on, props } from '@ngrx/store';

import { Delta } from 'quill';

import { QuillTemplate, QuillTemplateLocation } from '../../../generated/graphql.generated';

export const TEMPLATES_WITH_SUBJECT = new Set([
    QuillTemplateLocation.JobCommentSendEmailToCustomer,
    QuillTemplateLocation.DispatchSchedule,
    QuillTemplateLocation.EstimateSendEmail,
    QuillTemplateLocation.ContractSendEmail,
    QuillTemplateLocation.InvoiceSendEmail,
    QuillTemplateLocation.ReceiptSendEmail,
]);

export const TemplatesActions = createActionGroup({
  source: 'Templates',
  events: {
    'Create Template Button Clicked': props<{ location?: QuillTemplateLocation }>(),
    'Edit Template Button Clicked': props<{ template: QuillTemplate; id?: string }>(),

    // `requestId` correlates a save with its outcome so that the dialog that
    // started it can tell its own result apart from any other save in flight
    // (the table's inline save and default toggle dispatch the same actions).
    // Dispatchers that do not care about the outcome simply omit it.
    'Template Creation Requested': props<{ input: { name: string; contents: string; subjectContents?: string | null;
        location: QuillTemplateLocation }; requestId?: string }>(),
    'Template Created': props<{ template: QuillTemplate; requestId?: string }>(),
    'Template Creation Failed': props<{ error: Error; requestId?: string }>(),

    'Template Update Requested': props<{ input: { ids: string[];
        update: { name?: string; contents?: string; subjectContents?: string | null;
            location: QuillTemplateLocation; isDefault?: boolean} };
        requestId?: string }>(),
    'Template Updated': props<{ template: QuillTemplate; requestId?: string }>(),
    'Template Update Failed': props<{ error: Error; requestId?: string }>(),

    'Template Deletion Requested': props<{ id: string }>(),
    'Template Deleted': props<{ id: string }>(),
    'Template Deletion Failed': props<{ error: Error }>(),

    // Inline table editing actions
    'Template Content Edited': props<{ templateId: string; contents?: Delta; subjectContents?: Delta }>(),
    'Discard Button Clicked': props<{template: QuillTemplate}>(),
    'Save Button Clicked': props<{template: QuillTemplate}>(),
  }
});

const templatesInitialState = {
    templateContentEdits: {} as Record<string, {
        contents: Delta;
        subjectContents: Delta | null;
    }>,
};

export type TemplatesState = typeof templatesInitialState;

export const templatesFeature = createFeature({
	name: 'Templates',
	reducer: createReducer(
		templatesInitialState,
		on(TemplatesActions.templateContentEdited, (state, { contents, templateId, subjectContents }): TemplatesState => {
            if (!templateId || (!contents && !subjectContents)) {
                return state;
            }

            const updatedTemplateSpecificContent = {
                ...(state.templateContentEdits[templateId] || {}),
                ...(contents && { contents }),
                ...(subjectContents && { subjectContents })
            };

            return {
                ...state,
                templateContentEdits: {
                    ...state.templateContentEdits,
                    [templateId]: updatedTemplateSpecificContent
                },
            };
        }),
        on(TemplatesActions.discardButtonClicked, (state): TemplatesState => {
            return {
                ...state,
                templateContentEdits: {}
            };
        }),
        on(TemplatesActions.templateUpdated, (state, { template: updatedTemplate }): TemplatesState => {
            const newTemplateContentEdits = { ...state.templateContentEdits };

            // Delete the property from the new copy
            delete newTemplateContentEdits[updatedTemplate.id];

            return {
                ...state,
                templateContentEdits: newTemplateContentEdits,
            };
        })
	)
});
